/**
 * CommandCenter — the redesigned Dashboard (iter265).
 *
 * Replaces the previous customizable widget grid with one opinionated,
 * daily-engagement-focused layout. Mirrors the user-approved mockup:
 *
 *   [ Hero composer w/ "Ask your business anything…" + 8 prompt chips ]
 *   [ Start My Day brief    |   AI Quick Actions ]
 *   [ Money Snapshot (4 tiles) ]
 *   [ My Tasks (5)          |   Alerts (5) ]
 *   [ Documents & Sigs (3)  |   Recent Activity (6)  |  Top Projects (3) ]
 *   [ Anomalies (3) | Tax Countdown | Bank Recon | Slow Payers | Memory Pins ]
 *
 * All sections render their own empty-state hint when `data_state !== 'ready'`,
 * pulled from the backend payload so onboarding copy stays consistent.
 *
 * Hero composer hands off to AI Hub via navigate('/ai-hub?seed=<prompt>').
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Send, Sparkles, ArrowRight, TrendingUp, AlertCircle, FileSignature, Activity,
  CheckSquare, PieChart, BarChart3, Mail, FileText, Calendar, ListChecks,
  Clock, Brain, Building2, Banknote, Zap, RefreshCw, Wallet, ReceiptText,
  Landmark, Loader2, HelpCircle, ChevronDown, CalendarRange, Upload,
} from 'lucide-react';
import { apiFetch } from '../fetchInterceptor';
import { useWorkspace } from '../context/WorkspaceContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BriefSettingsSheet } from '../components/command-center/BriefSettingsSheet';
import { TodaysMeetingsCard } from '../components/command-center/TodaysMeetingsCard';
import PaymentsSetupBanner from '../components/dashboard/PaymentsSetupBanner';
import DocumentUploadModal from '../components/DocumentUploadModal';
import CashFlowForecastWidget from '../components/dashboard/CashFlowForecastWidget';
import RevenueExpensesWidget from '../components/dashboard/RevenueExpensesWidget';
import AITourGuideLauncher from '../components/dashboard/AITourGuideLauncher';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROMPT_ICONS = {
  'trending-up': TrendingUp,
  'pie-chart': PieChart,
  'alert-circle': AlertCircle,
  'bar-chart-3': BarChart3,
  'mail': Mail,
  'file-text': FileText,
  'calendar': Calendar,
  'list-checks': ListChecks,
};

// Per-tile accent. Each color pair drives an icon emblem gradient AND
// the tile's ring tint. Chosen for legibility on light, dark, midnight,
// and classic themes — emerald / blue / amber / rose all have proven
// 700/400 split contrast that respects the existing theme variables.
const MONEY_ACCENT = {
  cash:         { from: 'from-emerald-500', to: 'to-teal-600',  text: 'text-emerald-700 dark:text-emerald-300', ring: 'ring-emerald-500/30',  icon: Wallet,      bgSoft: 'bg-emerald-50 dark:bg-emerald-950/30 midnight:bg-emerald-950/40 classic:bg-emerald-50' },
  receivables:  { from: 'from-blue-500',    to: 'to-indigo-600', text: 'text-blue-700 dark:text-blue-300',       ring: 'ring-blue-500/30',     icon: TrendingUp,  bgSoft: 'bg-blue-50 dark:bg-blue-950/30 midnight:bg-blue-950/40 classic:bg-blue-50' },
  payables:     { from: 'from-amber-500',   to: 'to-orange-600', text: 'text-amber-700 dark:text-amber-300',     ring: 'ring-amber-500/30',    icon: ReceiptText, bgSoft: 'bg-amber-50 dark:bg-amber-950/30 midnight:bg-amber-950/40 classic:bg-amber-50' },
  // Profit tile — green when positive, amber/rose visually handled by `tone`
  // on the value text rather than the ring (the ring stays consistent so the
  // grid feels balanced).
  profit:       { from: 'from-violet-500',  to: 'to-purple-600', text: 'text-violet-700 dark:text-violet-300',   ring: 'ring-violet-500/30',   icon: TrendingUp,  bgSoft: 'bg-violet-50 dark:bg-violet-950/30 midnight:bg-violet-950/40 classic:bg-violet-50' },
};

// Reusable theme-aware gradient palette for section header icon chips.
// `from-/to-` pairs work across all 4 themes because Tailwind colors are
// already saturation-tuned for both light & dark surfaces.
const ACCENT_CHIPS = {
  emerald: 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-sm shadow-emerald-500/20',
  blue:    'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-sm shadow-blue-500/20',
  violet:  'bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-sm shadow-violet-500/20',
  amber:   'bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-sm shadow-amber-500/20',
  rose:    'bg-gradient-to-br from-rose-500 to-pink-600 text-white shadow-sm shadow-rose-500/20',
  zinc:    'bg-gradient-to-br from-zinc-500 to-zinc-700 text-white shadow-sm',
};

const TONE_RING_ALERT = {
  high:   'border-l-rose-500',
  medium: 'border-l-amber-500',
  low:    'border-l-zinc-400',
};

const fmtMoney = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1000) return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return `$${n.toFixed(2)}`;
};

const relativeTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const m = Math.floor((Date.now() - d.getTime()) / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
};

const fmtDue = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ─── Sub-components ───────────────────────────────────────────────────────

const SectionHeader = ({ icon: Icon, title, accent = 'emerald', linkTo, linkLabel }) => {
  const navigate = useNavigate();
  const chipClass = ACCENT_CHIPS[accent] || ACCENT_CHIPS.emerald;
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2.5">
        <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center', chipClass)}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-700 dark:text-zinc-300">
          {title}
        </h2>
      </div>
      {linkTo && (
        <button
          onClick={() => navigate(linkTo)}
          className="text-[11px] text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 flex items-center gap-0.5"
          data-testid={`cc-link-${title.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {linkLabel || 'View all'} <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

const EmptyHint = ({ hint, icon: Icon = Sparkles }) => (
  <div className="text-center py-6 px-3 text-[12px] text-zinc-500 dark:text-zinc-400 leading-relaxed">
    <Icon className="w-6 h-6 mx-auto mb-2 opacity-40" />
    {hint}
  </div>
);

// Tiny inline SVG sparkline. Pure visual — no interactivity.
const Sparkline = ({ points = [], color = '#10b981' }) => {
  if (!Array.isArray(points) || points.length < 2) return null;
  const w = 80, h = 22;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const stepX = w / (points.length - 1);
  const path = points
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * stepX).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="mt-2 opacity-80">
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// Profit tile renders a timeframe dropdown inline. Refreshes via the
// dedicated /profit-tile endpoint so we don't re-run all 12 section
// builders just because the user changed "MTD" → "YTD".
const ProfitTile = ({ tile, workspaceId, onClick }) => {
  const navigate = useNavigate();
  const accent = MONEY_ACCENT.profit;
  const TileIcon = accent.icon;
  const [current, setCurrent] = useState(tile);
  const [customOpen, setCustomOpen] = useState(false);
  const [customStart, setCustomStart] = useState(tile.start_date || '');
  const [customEnd, setCustomEnd] = useState(tile.end_date || '');
  const [refreshing, setRefreshing] = useState(false);

  const setRange = async (rangeKey, start, end) => {
    if (!workspaceId) return;
    setRefreshing(true);
    try {
      const params = new URLSearchParams({ workspace_id: workspaceId, range: rangeKey });
      if (rangeKey === 'custom') {
        if (!start || !end) { setRefreshing(false); return; }
        params.set('start_date', start);
        params.set('end_date', end);
      }
      const res = await apiFetch(`${API_URL}/api/dashboard/command-center/profit-tile?${params}`);
      if (res.ok) setCurrent(await res.json());
    } finally {
      setRefreshing(false);
      setCustomOpen(false);
    }
  };

  const ranges = current.ranges_available || tile.ranges_available || [];

  // Profit value tone — green positive, rose negative
  const valueClass = current.value > 0
    ? 'text-emerald-700 dark:text-emerald-300'
    : current.value < 0
      ? 'text-rose-700 dark:text-rose-300'
      : accent.text;

  return (
    <div
      className={cn(
        'group relative rounded-2xl p-4 ring-1 text-left transition-all',
        'bg-white dark:bg-zinc-900/70 midnight:bg-[hsl(222,28%,12%)]',
        accent.ring,
      )}
      data-testid="cc-money-tile-profit"
    >
      <div className="flex items-start justify-between mb-3">
        <div className={cn(
          'w-9 h-9 rounded-xl flex items-center justify-center',
          'bg-gradient-to-br shadow-sm', accent.from, accent.to,
        )}>
          <TileIcon className="w-4 h-4 text-white" strokeWidth={2.25} />
        </div>

        {/* Timeframe dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-0.5 text-[10px] font-medium text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 rounded-md px-1.5 py-0.5 hover:bg-zinc-100 dark:hover:bg-white/5 transition"
              data-testid="cc-profit-range-trigger"
              onClick={(e) => e.stopPropagation()}
            >
              {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : current.range_label}
              <ChevronDown className="w-3 h-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {ranges.filter(r => r.key !== 'custom').map(r => (
              <DropdownMenuItem
                key={r.key}
                onClick={() => setRange(r.key)}
                data-testid={`cc-profit-range-${r.key}`}
              >
                {r.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setCustomOpen(true)}
              data-testid="cc-profit-range-custom"
            >
              <CalendarRange className="w-3.5 h-3.5 mr-1.5" /> Custom range…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <button
        onClick={onClick}
        className="block w-full text-left hover:scale-[1.01] transition-transform"
      >
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400 font-semibold mb-0.5">
          {current.label}
        </div>
        <div className={cn('text-2xl font-bold tabular-nums', valueClass)}>
          {fmtMoney(current.value)}
        </div>
        <div className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-1 truncate">
          {current.delta_text}
        </div>
      </button>
      {/* Auto-pivot indicator — backend silently moved the Profit tile
          to the user's most recent populated month because MTD was
          empty. Keep this distinct from the "uncategorized waiting"
          hint below: this one says "we already did the work for you,
          here's the real data". */}
      {current.auto_pivoted && (
        <div
          className="mt-2 inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 dark:text-amber-300"
          data-testid="cc-profit-auto-pivot-badge"
        >
          <AlertCircle className="w-3 h-3" />
          Showing your latest active period
        </div>
      )}
      {/* Actionable empty-state hint when value is $0. Three flavors:
          - uncategorized waiting → deep-link into Accounting Hub
          - data exists in another period → one-click switch the range
            (preset, e.g. YTD)
          - data exists in a specific past month → switch to that
            month via a custom range so the user immediately sees real
            numbers without leaving the dashboard. */}
      {current.hint && (current.hint_link || current.hint_action === 'switch_range' || current.hint_action === 'switch_custom_range') && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (current.hint_action === 'switch_range' && current.hint_value) {
              setRange(current.hint_value);
            } else if (current.hint_action === 'switch_custom_range' && current.hint_value?.start_date && current.hint_value?.end_date) {
              setRange('custom', current.hint_value.start_date, current.hint_value.end_date);
            } else if (current.hint_link) {
              navigate(current.hint_link);
            }
          }}
          className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 dark:text-amber-300 hover:text-amber-600 dark:hover:text-amber-200 underline underline-offset-2 text-left"
          data-testid="cc-profit-hint-link"
        >
          {current.hint} →
        </button>
      )}

      {/* Custom-range popover */}
      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="max-w-sm" data-testid="cc-profit-custom-dialog">
          <DialogHeader>
            <DialogTitle>Custom date range</DialogTitle>
            <DialogDescription>Pick the window for the Profit tile.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">From</label>
              <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="cc-profit-custom-start" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">To</label>
              <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="cc-profit-custom-end" />
            </div>
            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              onClick={() => setRange('custom', customStart, customEnd)}
              disabled={!customStart || !customEnd}
              data-testid="cc-profit-custom-apply"
            >
              Apply
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const MoneyTile = ({ tile, onClick, workspaceId }) => {
  // Profit is a special-case tile — owns its own dropdown for timeframe.
  if (tile.key === 'profit') {
    return <ProfitTile tile={tile} workspaceId={workspaceId} onClick={onClick} />;
  }

  const accent = MONEY_ACCENT[tile.key] || MONEY_ACCENT.cash;
  const TileIcon = accent.icon;
  return (
    <button
      onClick={onClick}
      className={cn(
        'group relative rounded-2xl p-4 ring-1 text-left transition-all',
        'hover:scale-[1.02] hover:shadow-lg hover:-translate-y-0.5',
        'bg-white dark:bg-zinc-900/70 midnight:bg-[hsl(222,28%,12%)]',
        accent.ring,
      )}
      data-testid={`cc-money-tile-${tile.key}`}
    >
      <div className={cn(
        'w-9 h-9 rounded-xl flex items-center justify-center mb-3',
        'bg-gradient-to-br shadow-sm', accent.from, accent.to,
      )}>
        <TileIcon className="w-4 h-4 text-white" strokeWidth={2.25} />
      </div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400 font-semibold mb-0.5">
        {tile.label}
      </div>
      <div className={cn('text-2xl font-bold tabular-nums', accent.text)}>
        {fmtMoney(tile.value)}
      </div>
      <div className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-1">
        {tile.delta_text}
      </div>
      {/* Sparkline shows up on the Cash tile (backend provides points). */}
      {tile.key === 'cash' && tile.sparkline && (
        <Sparkline points={tile.sparkline} color="#10b981" />
      )}
      <ArrowRight className="absolute top-3 right-3 w-3.5 h-3.5 opacity-0 group-hover:opacity-60 transition-opacity" />
    </button>
  );
};

// Theme-aware AI Brief card — proactively narrates the week.
const BriefMeCard = ({ brief, loading, onRegenerate, emailOptIn, cadenceLabel, onPreviewEmail, onOpenSettings }) => (
  <div
    className={cn(
      'relative overflow-hidden rounded-2xl p-4 ring-1',
      'bg-gradient-to-br from-violet-50 via-white to-emerald-50',
      'dark:from-violet-950/30 dark:via-zinc-900/70 dark:to-emerald-950/20',
      'midnight:from-violet-950/40 midnight:via-[hsl(222,28%,12%)] midnight:to-emerald-950/30',
      'classic:from-violet-50 classic:via-white classic:to-emerald-50',
      'ring-violet-200/60 dark:ring-violet-900/40 midnight:ring-violet-700/30',
    )}
    data-testid="cc-brief-card"
  >
    <div className="flex items-start justify-between gap-3 mb-2">
      <div className="flex items-center gap-2.5">
        <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center', ACCENT_CHIPS.violet)}>
          <Sparkles className="w-3.5 h-3.5" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-violet-700 dark:text-violet-300 midnight:text-violet-200">
            Weekly Brief
          </div>
          {brief?.iso_week && (
            <div className="text-[10px] text-zinc-500 dark:text-zinc-400">{brief.iso_week}</div>
          )}
        </div>
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={onRegenerate}
        disabled={loading}
        className="h-7 px-2 text-[11px] text-violet-700 dark:text-violet-300 hover:bg-violet-100/50 dark:hover:bg-violet-900/40"
        data-testid="cc-brief-regenerate"
      >
        {loading ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <><RefreshCw className="w-3 h-3 mr-1" /> Brief me again</>
        )}
      </Button>
    </div>
    <p
      className="text-sm leading-relaxed text-zinc-800 dark:text-zinc-200 pl-9"
      data-testid="cc-brief-text"
    >
      {loading && !brief?.text ? (
        <span className="inline-flex items-center gap-2 text-zinc-500">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Studio AI is reviewing your week…
        </span>
      ) : (
        brief?.text || 'No brief available yet. Click "Brief me again" to generate one.'
      )}
    </p>
    {/* Settings footer — opens the BriefSettingsSheet to customize cadence,
        topics, delivery time, and toggle the subscription. */}
    <div
      className="mt-3 ml-9 flex items-center justify-between gap-3 pt-2 border-t border-violet-200/40 dark:border-violet-900/30 midnight:border-violet-700/30"
      data-testid="cc-brief-email-optin-row"
    >
      <div className="flex items-center gap-2 text-[11px] text-zinc-600 dark:text-zinc-400">
        <Mail className="w-3 h-3 opacity-70" />
        {emailOptIn ? (
          <span>
            Subscribed{' '}
            <span className="text-zinc-400">· {cadenceLabel}</span>
          </span>
        ) : (
          <span>Not subscribed yet</span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onPreviewEmail}
          className="text-[11px] text-violet-700 dark:text-violet-300 hover:underline"
          data-testid="cc-brief-email-preview-btn"
        >
          Preview email
        </button>
        <button
          type="button"
          onClick={onOpenSettings}
          className="text-[11px] font-medium text-violet-700 dark:text-violet-300 hover:underline"
          data-testid="cc-brief-customize-btn"
        >
          Customize →
        </button>
      </div>
    </div>
  </div>
);

const TaskRow = ({ task }) => {
  const today = new Date().toISOString().slice(0, 10);
  const overdue = task.due_date && task.due_date < today;
  return (
    <div className="flex items-center justify-between gap-3 py-2 group hover:bg-zinc-50 dark:hover:bg-white/5 -mx-2 px-2 rounded transition-colors" data-testid="cc-task-row">
      <div className="flex items-start gap-2 min-w-0">
        <CheckSquare className="w-3.5 h-3.5 mt-0.5 text-zinc-400 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm text-zinc-900 dark:text-zinc-100 truncate">{task.title}</div>
          {task.due_date && (
            <div className={cn('text-[10px] mt-0.5', overdue ? 'text-rose-600 font-semibold' : 'text-zinc-500')}>
              {overdue ? 'Overdue · ' : 'Due '}{fmtDue(task.due_date)}
            </div>
          )}
        </div>
      </div>
      {task.priority && (
        <Badge variant="outline" className={cn(
          'text-[9px] px-1.5 py-0 capitalize shrink-0',
          task.priority === 'high' && 'border-rose-300 text-rose-700 dark:border-rose-800',
          task.priority === 'medium' && 'border-amber-300 text-amber-700 dark:border-amber-800',
        )}>
          {task.priority}
        </Badge>
      )}
    </div>
  );
};

const AlertRow = ({ alert }) => {
  const navigate = useNavigate();
  const sevColor = TONE_RING_ALERT[alert.severity] || 'border-l-zinc-300';
  return (
    <div className={cn('border-l-2 pl-3 py-2', sevColor)} data-testid={`cc-alert-${alert.kind}`}>
      <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{alert.title}</div>
      <div className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-0.5">{alert.body}</div>
      {alert.action_link && (
        <button
          onClick={() => navigate(alert.action_link)}
          className="text-[11px] mt-1 text-emerald-700 dark:text-emerald-400 hover:underline flex items-center gap-0.5"
        >
          {alert.action_label} <ArrowRight className="w-2.5 h-2.5" />
        </button>
      )}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────

export default function CommandCenter() {
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [composerText, setComposerText] = useState('');
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [cadence, setCadence] = useState('weekly');

  const wsId = currentWorkspace?.workspace_id;
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = async () => {
    if (!wsId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const r = await apiFetch(`${API_URL}/api/dashboard/command-center?workspace_id=${wsId}&profit_range=auto`);
      if (r.ok) {
        setData(await r.json());
      } else if (r.status === 401 || r.status === 403) {
        // Session likely expired — surface that clearly instead of the
        // misleading "Pick a workspace" empty state.
        setLoadError('session_expired');
      } else {
        setLoadError(`server_${r.status}`);
      }
    } catch (e) {
      setLoadError('network');
    } finally {
      setLoading(false);
    }
  };

  const loadBrief = async () => {
    if (!wsId) return;
    setBriefLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/api/dashboard/command-center/brief?workspace_id=${wsId}`);
      if (r.ok) setBrief(await r.json());
    } finally {
      setBriefLoading(false);
    }
  };

  const regenerateBrief = async () => {
    if (!wsId) return;
    setBriefLoading(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/dashboard/command-center/brief/regenerate?workspace_id=${wsId}`,
        { method: 'POST' },
      );
      if (r.ok) setBrief(await r.json());
    } finally {
      setBriefLoading(false);
    }
  };

  const loadBriefPrefs = async () => {
    if (!wsId) return;
    try {
      const r = await apiFetch(
        `${API_URL}/api/dashboard/command-center/brief/preferences?workspace_id=${wsId}`,
      );
      if (r.ok) {
        const data = await r.json();
        const p = data.preferences || {};
        setEmailOptIn(!!p.subscribed);
        setCadence(p.cadence || 'weekly');
      }
    } catch (e) { /* silent */ }
  };

  const openEmailPreview = async () => {
    if (!wsId) return;
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewHtml('');
    try {
      const r = await apiFetch(
        `${API_URL}/api/dashboard/command-center/brief/email-preview?workspace_id=${wsId}`,
      );
      if (r.ok) {
        const data = await r.json();
        setPreviewHtml(data.html || '');
      } else {
        setPreviewHtml('<p style="padding:24px;color:#dc2626;">Could not load preview.</p>');
      }
    } catch (e) {
      setPreviewHtml('<p style="padding:24px;color:#dc2626;">Could not load preview.</p>');
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);
  useEffect(() => { loadBrief(); /* eslint-disable-next-line */ }, [wsId]);
  useEffect(() => { loadBriefPrefs(); /* eslint-disable-next-line */ }, [wsId]);

  const handlePromptClick = (prompt) => {
    navigate(`/ai-hub?seed=${encodeURIComponent(prompt)}`);
  };

  const handleSend = () => {
    if (!composerText.trim()) return;
    handlePromptClick(composerText);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-sm text-zinc-500 flex items-center gap-2">
          <RefreshCw className="w-4 h-4 animate-spin" /> Building your command center…
        </div>
      </div>
    );
  }

  if (!data) {
    // Distinguish "no workspace selected" from "fetch actually failed" so
    // the user isn't told to pick a workspace they've already picked.
    if (!wsId) {
      return (
        <div className="flex items-center justify-center min-h-[60vh] text-zinc-500">
          Pick a workspace from the top bar to see your command center.
        </div>
      );
    }
    if (loadError === 'session_expired') {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-center px-4">
          <p className="text-sm text-zinc-500">Your session expired. Please sign in again.</p>
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
            data-testid="cc-relogin-btn"
          >
            Sign in
          </button>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-center px-4">
        <p className="text-sm text-zinc-500">
          Couldn't load your command center{loadError ? ` (${loadError})` : ''}.
        </p>
        <button
          onClick={load}
          className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
          data-testid="cc-retry-btn"
        >
          Try again
        </button>
      </div>
    );
  }

  const p = data.personalization;

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 via-white to-zinc-50 dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-900 pb-24" data-testid="command-center-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* Payments setup nudge — only renders when Stripe Connect isn't
            fully active. Dismissible per-workspace via localStorage. */}
        <PaymentsSetupBanner workspaceId={wsId} />
        <DocumentUploadModal
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          workspaceId={wsId}
          onUploaded={() => {}}
        />

        {/* ── HERO ────────────────────────────────────────────────────── */}
        <div
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-blue-50 dark:from-emerald-950/30 dark:via-zinc-900 dark:to-blue-950/30 ring-1 ring-emerald-200/50 dark:ring-emerald-900/40 p-6 sm:p-8"
          data-testid="cc-hero"
        >
          {/* Decorative grid pattern — CountBucks signature, replaces the mountain mockup */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.03] dark:opacity-[0.06]"
            style={{
              backgroundImage:
                'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
              backgroundSize: '20px 20px',
            }}
          />
          {/* Subtle glow accent */}
          <div
            aria-hidden="true"
            className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-emerald-400/10 blur-3xl"
          />

          <div className="relative space-y-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-zinc-100" data-testid="cc-greeting">
                  {p.greeting}, {p.user_name}
                </h1>
                <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1" data-testid="cc-insight">
                  {p.tasks_due_today > 0 && (
                    <span className="font-semibold text-zinc-900 dark:text-zinc-100">
                      {p.tasks_due_today} task{p.tasks_due_today === 1 ? '' : 's'} due today.{' '}
                    </span>
                  )}
                  {p.insight}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUploadOpen(true)}
                className="shrink-0 gap-1.5"
                data-testid="cc-upload-docs-btn"
              >
                <Upload className="w-3.5 h-3.5" /> Upload documents
              </Button>
            </div>

            {/* Composer */}
            <div className="rounded-2xl bg-white dark:bg-zinc-900 ring-1 ring-zinc-200 dark:ring-white/10 shadow-sm overflow-hidden">
              <Textarea
                value={composerText}
                onChange={(e) => setComposerText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Ask your business anything — cash, customers, contracts, strategy…"
                rows={2}
                className="border-0 resize-none bg-transparent text-base focus-visible:ring-0 px-4 pt-3 pb-2 min-h-[60px]"
                data-testid="cc-composer-input"
              />
              <div className="flex items-center justify-between px-3 py-2 border-t border-zinc-100 dark:border-white/5">
                <div className="text-[10px] text-zinc-400">
                  Press Enter to send → opens Studio AI
                </div>
                <Button
                  size="sm"
                  onClick={handleSend}
                  disabled={!composerText.trim()}
                  className="h-7 gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black"
                  data-testid="cc-composer-send"
                >
                  <Send className="w-3 h-3" /> Send
                </Button>
              </div>
            </div>

            {/* Prompt chips */}
            <div className="flex flex-wrap gap-2" data-testid="cc-prompt-chips">
              {(data.quick_prompts || []).map((qp, i) => {
                const Icon = PROMPT_ICONS[qp.icon] || Sparkles;
                return (
                  <button
                    key={i}
                    onClick={() => handlePromptClick(qp.prompt)}
                    className={cn(
                      'group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium',
                      'bg-white/80 dark:bg-zinc-900/80 ring-1 ring-zinc-200 dark:ring-white/10',
                      'hover:ring-emerald-400/50 hover:bg-emerald-50 dark:hover:bg-emerald-950/30',
                      'text-zinc-700 dark:text-zinc-300 transition-all',
                    )}
                    data-testid={`cc-prompt-${i}`}
                  >
                    <Icon className="w-3 h-3" />
                    {qp.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── BRIEF ME (AI weekly narrative) ──────────────────────────── */}
        <BriefMeCard
          brief={brief}
          loading={briefLoading}
          onRegenerate={regenerateBrief}
          emailOptIn={emailOptIn}
          cadenceLabel={{daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly'}[cadence] || 'Weekly'}
          onPreviewEmail={openEmailPreview}
          onOpenSettings={() => setSettingsOpen(true)}
        />

        {/* ── MONEY SNAPSHOT ──────────────────────────────────────────── */}
        <section data-testid="cc-money-snapshot">
          <SectionHeader icon={Banknote} title="Money Snapshot" accent="emerald" linkTo="/banking" />
          {data.money_snapshot.data_state === 'empty' ? (
            <Card><CardContent><EmptyHint hint={data.money_snapshot.empty_hint} icon={Banknote} /></CardContent></Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {data.money_snapshot.tiles.map((t) => (
                <MoneyTile key={t.key} tile={t} workspaceId={wsId} onClick={() => navigate(t.link)} />
              ))}
            </div>
          )}
        </section>

        {/* ── CASH FLOW FORECAST (AI prediction) ──────────────────────── */}
        <section data-testid="cc-cash-flow-forecast">
          <CashFlowForecastWidget workspaceId={wsId} />
        </section>

        {/* ── REVENUE & EXPENSES (with prior-period comparison) ──────── */}
        <section data-testid="cc-revenue-expenses">
          <RevenueExpensesWidget workspaceId={wsId} />
        </section>

        {/* ── 2-COL: TASKS + ALERTS ──────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section data-testid="cc-my-tasks">
            <SectionHeader icon={ListChecks} title="My Tasks" accent="emerald" linkTo="/tasks" />
            <Card>
              <CardContent className="py-3">
                {data.my_tasks.data_state === 'empty' ? (
                  <EmptyHint hint={data.my_tasks.empty_hint} icon={ListChecks} />
                ) : (
                  <div className="divide-y divide-zinc-100 dark:divide-white/5">
                    {data.my_tasks.items.map((t) => <TaskRow key={t.task_id} task={t} />)}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          <section data-testid="cc-alerts">
            <SectionHeader icon={AlertCircle} title="Alerts" accent="amber" />
            <Card>
              <CardContent className="py-3 space-y-1">
                {data.alerts.data_state === 'empty' ? (
                  <EmptyHint hint={data.alerts.empty_hint} icon={AlertCircle} />
                ) : (
                  data.alerts.items.map((a, i) => <AlertRow key={i} alert={a} />)
                )}
              </CardContent>
            </Card>
          </section>
        </div>

        {/* ── 3-COL: SIGS, ACTIVITY, PROJECTS ─────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section data-testid="cc-pending-sigs">
            <SectionHeader icon={FileSignature} title="Signatures" accent="blue" linkTo="/signatures" />
            <Card><CardContent className="py-3 space-y-2">
              {data.pending_signatures.data_state === 'empty' ? (
                <EmptyHint hint={data.pending_signatures.empty_hint} icon={FileSignature} />
              ) : data.pending_signatures.items.map((s) => (
                <div key={s.request_id} className="rounded-lg p-3 bg-zinc-50 dark:bg-white/5">
                  <div className="text-sm font-medium truncate">{s.title || 'Untitled'}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
                    Sent to {s.signer_name || s.signer_email}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/signatures?request=${s.request_id}`)}
                    className="mt-2 h-6 text-[10px] px-2"
                    data-testid="cc-sig-review"
                  >
                    Review & follow up
                  </Button>
                </div>
              ))}
            </CardContent></Card>
          </section>

          <section data-testid="cc-recent-activity">
            <SectionHeader icon={Activity} title="Recent Activity" accent="blue" />
            <Card><CardContent className="py-3 space-y-2">
              {data.recent_activity.data_state === 'empty' ? (
                <EmptyHint hint={data.recent_activity.empty_hint} icon={Activity} />
              ) : data.recent_activity.items.map((e) => (
                <div key={e.id} className="text-[11px] text-zinc-700 dark:text-zinc-300">
                  <span className="font-semibold">{e.actor_name}</span>{' '}
                  <span className="text-zinc-500">{e.title}</span>
                  <div className="text-[10px] text-zinc-400">{relativeTime(e.at)}</div>
                </div>
              ))}
            </CardContent></Card>
          </section>

          <section data-testid="cc-top-projects">
            <SectionHeader icon={Building2} title="Top Projects" accent="violet" linkTo="/ai-hub" linkLabel="Open AI Hub" />
            <Card><CardContent className="py-3 space-y-2">
              {data.top_projects.data_state !== 'ready' ? (
                <EmptyHint hint={data.top_projects.empty_hint} icon={Building2} />
              ) : data.top_projects.items.map((pr) => (
                <button
                  key={pr.project_id}
                  onClick={() => navigate(`/ai-hub?project=${pr.project_id}`)}
                  className="block w-full text-left rounded-lg p-2.5 hover:bg-zinc-50 dark:hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ background: pr.icon_color }} />
                    <div className="text-sm truncate">{pr.name}</div>
                  </div>
                  <div className="text-[10px] text-zinc-500 ml-4">{pr.thread_count || 0} conversations</div>
                </button>
              ))}
            </CardContent></Card>
          </section>
        </div>

        {/* ── BUSINESS HEALTH ROW ─────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section data-testid="cc-anomalies">
            <SectionHeader icon={Zap} title="AI Anomalies" accent="amber" />
            <Card><CardContent className="py-3 space-y-2">
              {data.anomalies.data_state === 'empty' ? (
                <EmptyHint hint={data.anomalies.empty_hint} icon={Zap} />
              ) : data.anomalies.items.map((a) => (
                <div key={a.account_id} className="flex items-center justify-between gap-3 py-1.5">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{a.category}</div>
                    <div className="text-[11px] text-zinc-500">{a.headline}</div>
                  </div>
                  <Badge className={cn(
                    'text-[10px] shrink-0',
                    a.direction === 'up'
                      ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400'
                      : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
                  )}>
                    {a.direction === 'up' ? '↑' : '↓'} {Math.abs(a.delta_pct)}%
                  </Badge>
                </div>
              ))}
            </CardContent></Card>
          </section>

          <section data-testid="cc-slow-payers">
            <SectionHeader icon={Clock} title="Slow Payers" accent="rose" linkTo="/invoices?filter=overdue" />
            <Card><CardContent className="py-3 space-y-2">
              {data.slow_payers.data_state === 'empty' ? (
                <EmptyHint hint={data.slow_payers.empty_hint} icon={Clock} />
              ) : data.slow_payers.items.map((sp) => (
                <div key={sp.customer_id} className="flex items-center justify-between gap-3 py-1">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{sp.customer_name}</div>
                    <div className="text-[11px] text-zinc-500">
                      {sp.invoice_count} invoice{sp.invoice_count === 1 ? '' : 's'} · oldest {fmtDue(sp.oldest_due)}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-rose-600 tabular-nums shrink-0">
                    {fmtMoney(sp.total_due)}
                  </div>
                </div>
              ))}
            </CardContent></Card>
          </section>
        </div>

        {/* Today's meetings — only renders when Google is connected. Lives
            in its own row so it doesn't leave a phantom grid cell on
            workspaces that haven't connected Google. */}
        <TodaysMeetingsCard />

        {/* ── TAX + RECON + MEMORY ROW ────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section data-testid="cc-tax-countdown">
            <SectionHeader icon={Calendar} title="Tax Countdown" accent="amber" />
            <Card><CardContent className="py-3 space-y-2">
              {data.tax_countdown.items.map((t, i) => (
                <div key={i} className="flex items-center justify-between gap-3">
                  <div className="text-sm truncate">{t.label}</div>
                  <Badge className={cn(
                    'text-[10px] shrink-0',
                    t.tone === 'critical' && 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400',
                    t.tone === 'warning' && 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
                    t.tone === 'neutral' && 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
                  )}>
                    {t.days_until}d
                  </Badge>
                </div>
              ))}
            </CardContent></Card>
          </section>

          <section data-testid="cc-bank-recon">
            <SectionHeader icon={RefreshCw} title="Bank Recon" accent="emerald" linkTo="/banking" />
            <Card><CardContent className="py-3 space-y-2">
              {data.bank_reconcile.data_state !== 'ready' ? (
                <EmptyHint hint={data.bank_reconcile.empty_hint} icon={Banknote} />
              ) : data.bank_reconcile.items.map((b) => (
                <div key={b.bank_id} className="flex items-center justify-between gap-3 py-1">
                  <div className="text-sm truncate">{b.name}</div>
                  <Badge className={cn(
                    'text-[10px] shrink-0',
                    b.tone === 'critical' && 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400',
                    b.tone === 'warning' && 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
                    b.tone === 'positive' && 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
                    b.tone === 'neutral' && 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
                  )}>
                    {b.days_since === null ? 'never' : `${b.days_since}d`}
                  </Badge>
                </div>
              ))}
            </CardContent></Card>
          </section>

          <section data-testid="cc-memory-pins">
            <SectionHeader icon={Brain} title="AI Memory" accent="violet" linkTo="/ai-hub" linkLabel="Manage memory" />
            <Card><CardContent className="py-3 space-y-2">
              {data.ai_memory_pins.data_state === 'empty' ? (
                <EmptyHint hint={data.ai_memory_pins.empty_hint} icon={Brain} />
              ) : data.ai_memory_pins.items.map((m) => (
                <div key={m.memory_id} className="text-[11px] text-zinc-700 dark:text-zinc-300 leading-relaxed">
                  <span className="font-semibold text-violet-700 dark:text-violet-400">{m.category || 'pin'}:</span>{' '}
                  {m.fact}
                </div>
              ))}
            </CardContent></Card>
          </section>
        </div>

      </div>

      {/* Sunday email preview modal — renders the exact HTML the loop sends */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0" data-testid="cc-brief-email-preview-modal">
          <DialogHeader className="px-5 pt-5 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Mail className="w-4 h-4 text-violet-600" />
              Sunday Brief — Email Preview
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500 dark:text-zinc-400">
              This is exactly what will land in your inbox every Sunday night if you stay subscribed.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-zinc-100 dark:bg-zinc-950">
            {previewLoading ? (
              <div className="flex items-center justify-center py-16 text-sm text-zinc-500">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Rendering preview…
              </div>
            ) : (
              <iframe
                title="Sunday Brief email preview"
                srcDoc={previewHtml}
                sandbox=""
                className="w-full h-[600px] bg-white"
                data-testid="cc-brief-email-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <BriefSettingsSheet
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onSaved={() => loadBriefPrefs()}
      />

      <AITourGuideLauncher />
    </div>
  );
}
